import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getChartKeyFromRequest } from './charts/simple/utils';
import { getChartProKeyFromRequest } from './charts/pro/utils';
import { SparkChartUtils } from './charts/spark/utils';
import { IChartSummary } from './charts/simple/models';
import { IChartProSummary } from './charts/pro/models';
import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';

export const CHARTS_STORE_NAME = `charts`;

export type ChartKeyRequest =
  | ReturnType<typeof getChartKeyFromRequest>
  | ReturnType<typeof getChartProKeyFromRequest>
  | ReturnType<typeof SparkChartUtils.getChartKeyFromRequest>;

export type MarketHour = Record<string, CmcWebApi.MarketInformation.MarketHoursModel1>;
export interface IChartStore extends Record<ChartKeyRequest, IChartSummary | IChartProSummary | MarketHour | undefined> {
  marketHour?: MarketHour;
}

export const defaultChartsStore: IChartStore = {};

export const createChartsStore = (initialState: IChartStore) =>
  createSlice({
    name: CHARTS_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultChartsStore,

      chartDataUpdated: (store, action: PayloadAction<{ key: ChartKeyRequest; response?: IChartSummary | IChartProSummary }>) => {
        if (action.payload.response) {
          store[action.payload.key] = action.payload.response;
        } else {
          delete store[action.payload.key];
        }
      },

      marketHourFetched: (store, action: PayloadAction<{ response: CmcWebApi.MarketInformation.MarketHoursModel1[] }>) => {
        store.marketHour = action.payload.response.reduce(
          (marketHour, marketHoursInfo) => ({ ...marketHour, [marketHoursInfo.ExchangeId]: marketHoursInfo }),
          {} as MarketHour
        );
      }
    }
  });

export const { actions, reducer } = createChartsStore(defaultChartsStore);

export const createStore = (initialStore: IChartStore = defaultChartsStore) =>
  configureStore({ reducer: { [CHARTS_STORE_NAME]: createChartsStore(initialStore).reducer } });

export const ChartStore = createStore();

export type IStore = ReturnType<typeof ChartStore.getState>;
